import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import { Search, Shield, Star, Heart, ArrowRight, CheckCircle2, Users, Sparkles, X, Briefcase, Zap, MessageCircle, Calendar, BadgeCheck, MapPin, Lock, MousePointerClick } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { AnimatedSection } from "@/hooks/useScrollAnimation";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import FreePriceTag from "@/components/FreePriceTag";
import FreeModeBanner from "@/components/FreeModeBanner";
import SectionFloatingIcons from "@/components/SectionFloatingIcons";
import GeoConsentDialog from "@/components/GeoConsentDialog";
import HomeImageGallery from "@/components/HomeImageGallery";
import HeroVideoBackground from "@/components/HeroVideoBackground";
import HeroExplosiveBanner from "@/components/HeroExplosiveBanner";
import BackToSchoolHero from "@/components/BackToSchoolHero";
import Seo from "@/components/Seo";

const Index = () => {
  const { user } = useAuth();
  const { getSetting } = useSiteSettings();
  const [promo, setPromo] = useState<any>(null);
  const [promoDismissed, setPromoDismissed] = useState(false);
  const backToSchoolModeEnabled = getSetting("back_to_school_mode_enabled", "false") === "true";
  const proMonthlyPrice = parseFloat(getSetting("pro_monthly_price", "6.99").replace(",", ".")) || 6.99;
  const proAnnualMonthlyPrice = parseFloat(getSetting("pro_annual_price_monthly", "4.99").replace(",", ".")) || 4.99;
  const proAnnualTotal = proAnnualMonthlyPrice * 12;
  const proAnnualSavingsPercent = Math.max(0, Math.round((1 - proAnnualMonthlyPrice / proMonthlyPrice) * 100));
  const formatPrice = (value: number) => value.toFixed(2).replace(".", ",");

  useEffect(() => {
    const fetchPromo = async () => {
      const { data } = await supabase
        .from("promotions")
        .select("name, promo_code, discount_value, discount_type, show_banner, banner_text, original_price, promo_price")
        .eq("show_banner", true)
        .order("created_at", { ascending: false })
        .limit(1);
      if (data && data.length > 0) setPromo(data[0]);
    };
    fetchPromo();
  }, []);

  return (
    <div className="min-h-screen bg-immersive">
      <Seo
        title="Garde d'enfants vérifiée près de chez vous"
        description="Trouvez une baby-sitter, nounou ou assistante maternelle de confiance. Profils vérifiés, réservation en ligne, 0% commission."
        path="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "Click&Gard",
          url: "https://clickandgard.fr",
          potentialAction: {
            "@type": "SearchAction",
            target: "https://clickandgard.fr/search?q={search_term_string}",
            "query-input": "required name=search_term_string",
          },
        }}
      />
      <GeoConsentDialog autoPrompt />
      <Navbar />

      <HeroExplosiveBanner />

      {/* Promo Banner */}
      {promo && promo.show_banner && !promoDismissed && (
        <div className="relative overflow-hidden bg-gradient-to-r from-primary via-secondary to-accent text-primary-foreground py-3 px-4">
          <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-secondary/20 animate-shimmer" />
          <div className="relative flex items-center justify-center gap-3 text-center flex-wrap">
            <Sparkles className="w-4 h-4 animate-pulse" />
            <span className="text-sm md:text-base font-semibold">
              {promo.banner_text || promo.name}
            </span>
            <span className="bg-primary-foreground/20 backdrop-blur-sm px-3 py-0.5 rounded-full text-sm font-bold">
              {promo.promo_price ?? 9.99} € <span className="line-through opacity-70 text-xs">{promo.original_price ?? 14.99} €</span>
            </span>
            {promo.promo_code && (
              <span className="font-mono font-bold bg-primary-foreground/15 px-2 py-0.5 rounded text-sm tracking-wider">
                {promo.promo_code}
              </span>
            )}
            <Link to="/auth?tab=register&role=pro">
              <Button size="sm" className="rounded-full text-xs font-semibold h-7 px-4 bg-primary-foreground/20 hover:bg-primary-foreground/30 text-primary-foreground border-0">
                En profiter <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            </Link>
          </div>
          <button onClick={() => setPromoDismissed(true)} className="absolute right-3 top-1/2 -translate-y-1/2 text-primary-foreground/60 hover:text-primary-foreground transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* ===== TARIFS MIS EN AVANT ===== */}
      <section aria-label="Tarifs Click&Gard" className="relative z-20 border-y border-primary/15 bg-gradient-to-r from-primary/10 via-background/95 to-secondary/10 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-[1fr_1fr_1.15fr_auto] items-stretch">
            <div className="rounded-2xl border border-accent/30 bg-accent/10 px-4 py-3 flex flex-col justify-center">
              <span className="text-[11px] font-extrabold uppercase tracking-[0.16em] text-muted-foreground">Parents</span>
              <span className="text-xl font-black text-accent">100% gratuit</span>
              <span className="text-xs text-muted-foreground">Accès à la plateforme sans abonnement</span>
            </div>

            <div className="rounded-2xl border border-primary/25 bg-card/80 px-4 py-3 flex flex-col justify-center shadow-sm">
              <span className="text-[11px] font-extrabold uppercase tracking-[0.16em] text-muted-foreground">PRO mensuel</span>
              <FreePriceTag variant="inline" hideBadge>
                <span className="text-xl font-black text-foreground">{formatPrice(proMonthlyPrice)} €<span className="text-sm font-semibold text-muted-foreground">/mois</span></span>
              </FreePriceTag>
              <span className="text-xs text-muted-foreground">Sans engagement</span>
            </div>

            <div className="relative rounded-2xl border-2 border-secondary/40 bg-secondary/10 px-4 py-3 flex flex-col justify-center shadow-neon">
              {proAnnualSavingsPercent > 0 && (
                <span className="absolute -top-2.5 right-3 rounded-full bg-gradient-to-r from-primary to-secondary px-2.5 py-1 text-[10px] font-black text-primary-foreground shadow-md">
                  -{proAnnualSavingsPercent}%
                </span>
              )}
              <span className="text-[11px] font-extrabold uppercase tracking-[0.16em] text-muted-foreground">PRO annuel</span>
              <FreePriceTag variant="inline" hideBadge>
                <span className="text-xl font-black text-gradient-neon">{formatPrice(proAnnualMonthlyPrice)} €<span className="text-sm font-semibold text-muted-foreground">/mois</span></span>
              </FreePriceTag>
              <span className="text-xs text-muted-foreground">{formatPrice(proAnnualTotal)} € facturés par an</span>
            </div>

            <Link to="/auth?tab=register&role=pro" className="sm:col-span-2 lg:col-span-1">
              <Button className="w-full h-full min-h-14 rounded-2xl px-5 font-extrabold shadow-neon bg-gradient-to-r from-primary to-secondary hover:opacity-90">
                Voir les abonnements <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Mode Rentrée : remplace uniquement le hero, sans modifier l’accueil historique */}
      {backToSchoolModeEnabled && <BackToSchoolHero />}

      {/* ===== HERO avec vidéo en arrière-plan ===== */}
      <section className={`relative overflow-hidden min-h-[88vh] items-center ${backToSchoolModeEnabled ? "hidden" : "flex"}`}>
        <HeroVideoBackground />

        <div className="relative w-full px-4 md:px-6 lg:px-8 py-20 md:py-28">
          <div className="max-w-3xl mx-auto text-center">
            <AnimatedSection animation="scale" delay={0}>
              <div className="flex flex-col items-center gap-4 mb-8">
                <img
                  alt="Click&Gard"
                  src="/lovable-uploads/logo_click_gard_entete.png"
                  className="w-44 h-44 md:w-60 md:h-60 object-contain drop-shadow-[0_0_40px_rgba(123,47,255,0.55)]"
                />
                {getSetting("hero_badge_enabled", "true") === "true" && (
                  <div className="inline-flex items-center gap-2 bg-background/40 backdrop-blur-md border border-white/15 px-4 py-1.5 rounded-full text-xs md:text-sm font-semibold text-white shadow-lg">
                    <Sparkles className="w-3.5 h-3.5" />
                    {getSetting("hero_badge_text", "Lancement juin 2026")}
                  </div>
                )}
              </div>
            </AnimatedSection>

            <AnimatedSection animation="fade-up" delay={100}>
              <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold leading-[1.08] mb-6 tracking-tight text-white drop-shadow-[0_2px_20px_rgba(0,0,0,0.55)]">
                Trouver une garde d'enfant
                <br />
                <span className="text-gradient-neon">n'a jamais été aussi simple</span>
              </h1>
            </AnimatedSection>

            <AnimatedSection animation="fade-up" delay={200}>
              <p className="text-base md:text-xl text-white/90 mb-10 leading-relaxed max-w-2xl mx-auto drop-shadow-[0_2px_12px_rgba(0,0,0,0.5)]">
                Click&Gard met en relation les parents et les professionnels de la garde d'enfants, partout en France.
              </p>
            </AnimatedSection>

            <AnimatedSection animation="fade-up" delay={350}>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to={user ? "/search" : "/auth?tab=register&role=parent"}>
                  <Button size="lg" className="w-full sm:w-auto text-base px-8 rounded-2xl h-14 font-bold shadow-neon bg-gradient-to-r from-primary via-secondary to-primary hover:opacity-90 transition-all duration-300 btn-neon btn-neon-pulse led-neon">
                    <Search className="w-5 h-5 mr-2" />
                    Trouver une garde
                  </Button>
                </Link>
                <Link to="/auth?tab=register&role=pro">
                  <Button size="lg" className="w-full sm:w-auto text-base px-8 rounded-2xl h-14 font-bold shadow-neon bg-gradient-to-r from-[#FF4D9D] via-[#E850A1] to-[#FF4D9D] hover:opacity-90 transition-all duration-300 btn-neon btn-neon-pulse led-neon led-neon-pink">
                    <Briefcase className="w-5 h-5 mr-2" />
                    Devenir professionnel
                  </Button>
                </Link>
              </div>
            </AnimatedSection>
          </div>
        </div>

        <div className="neon-line w-1/2 mx-auto" />
      </section>

      {/* ===== PARENT / PRO CARDS ===== */}
      <section className="py-14 lg:py-20 relative overflow-hidden bg-section-violet">
        <SectionFloatingIcons variant={0} />
        <div className="relative w-full px-4 md:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            <AnimatedSection animation="fade-up" delay={0}>
              <div className="card-neon rounded-3xl p-8 h-full flex flex-col group">
                <div className="w-14 h-14 rounded-2xl bg-primary/20 border border-primary/30 flex items-center justify-center mb-5 glow-violet group-hover:scale-110 transition-transform">
                  <Heart className="w-7 h-7 text-primary" />
                </div>
                <h3 className="font-display text-2xl font-extrabold text-foreground mb-2">Je suis parent</h3>
                <p className="text-muted-foreground leading-relaxed mb-6 flex-1">
                  Publiez votre besoin ou réservez un créneau auprès d'un professionnel disponible.
                </p>
                <Link to={user ? "/search" : "/auth?tab=register&role=parent"}>
                  <Button size="lg" className="w-full rounded-2xl h-12 font-bold bg-gradient-to-r from-primary via-secondary to-primary text-primary-foreground shadow-neon">
                    <Search className="w-4 h-4 mr-2" />
                    Trouver une garde
                  </Button>
                </Link>
              </div>
            </AnimatedSection>

            <AnimatedSection animation="fade-up" delay={150}>
              <div className="card-neon rounded-3xl p-8 h-full flex flex-col group">
                <div className="w-14 h-14 rounded-2xl bg-secondary/20 border border-secondary/30 flex items-center justify-center mb-5 glow-pink group-hover:scale-110 transition-transform">
                  <Briefcase className="w-7 h-7 text-secondary" />
                </div>
                <h3 className="font-display text-2xl font-extrabold text-foreground mb-2">Je suis professionnel</h3>
                <p className="text-muted-foreground leading-relaxed mb-6 flex-1">
                  Créez votre profil, indiquez vos disponibilités et recevez des demandes de garde.
                </p>
                <Link to="/auth?tab=register&role=pro">
                  <Button size="lg" variant="outline" className="w-full rounded-2xl h-12 font-bold border-2 border-primary/30 text-primary hover:bg-primary hover:text-primary-foreground">
                    <Briefcase className="w-4 h-4 mr-2" />
                    Créer mon profil pro
                  </Button>
                </Link>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>


      {/* ===== 3 AVANTAGES ===== */}
      <section className="py-16 lg:py-20 relative overflow-hidden bg-section-violet">
        <SectionFloatingIcons variant={1} />
        <div className="neon-orb neon-orb-violet w-[400px] h-[400px] -top-[100px] -right-[100px]" />
        <div className="relative w-full px-4 md:px-6 lg:px-8">
          <AnimatedSection className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-extrabold text-foreground mb-3 tracking-tight">
              Pourquoi <span className="text-gradient-neon">Click&Gard</span> ?
            </h2>
          </AnimatedSection>
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {[
              { icon: Shield, title: "Sécurité maximale", desc: "Profils vérifiés, identité confirmée, avis certifiés.", iconColor: "text-primary", glow: "glow-violet" },
              { icon: CheckCircle2, title: "Profils vérifiés", desc: "Chaque professionnel passe par un processus de vérification.", iconColor: "text-accent", glow: "glow-blue" },
              { icon: Zap, title: "Réservation rapide", desc: "Trouvez et réservez en quelques clics, sans stress.", iconColor: "text-secondary", glow: "glow-pink" },
            ].map((item, i) => (
              <AnimatedSection key={item.title} delay={i * 150} animation="fade-up">
                <div className="card-neon rounded-2xl p-8 text-center group cursor-default h-full">
                  <div className={`w-16 h-16 rounded-2xl bg-primary/20 border border-primary/30 flex items-center justify-center mx-auto mb-5 group-hover:scale-110 transition-transform duration-300 ${item.glow}`}>
                    <item.icon className={`w-8 h-8 ${item.iconColor}`} />
                  </div>
                  <h3 className="text-lg font-bold text-foreground mb-2">{item.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{item.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* ===== COMMENT ÇA MARCHE ===== */}
      <section className="py-16 lg:py-20 relative overflow-hidden bg-section-pink">
        <SectionFloatingIcons variant={2} />
        <div className="neon-orb neon-orb-pink w-[350px] h-[350px] -bottom-[80px] -left-[80px]" />
        <div className="relative w-full px-4 md:px-6 lg:px-8">
          <AnimatedSection className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-extrabold text-foreground mb-3 tracking-tight">
              Comment ça <span className="text-gradient-neon">marche</span> ?
            </h2>
            <p className="text-muted-foreground text-lg">3 étapes simples</p>
          </AnimatedSection>
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {[
              { step: "1", title: "Publiez votre demande", desc: "Décrivez votre besoin de garde en quelques clics.", icon: MessageCircle, color: "text-primary" },
              { step: "2", title: "Recevez des propositions", desc: "Des professionnels vérifiés vous contactent.", icon: Users, color: "text-secondary" },
              { step: "3", title: "Réservez", desc: "Choisissez et réservez en toute confiance.", icon: Calendar, color: "text-accent" },
            ].map((item, i) => (
              <AnimatedSection key={item.step} delay={i * 200} animation="fade-up" className="text-center group">
                <div className="relative inline-flex mb-6">
                  <div className="w-18 h-18 rounded-2xl bg-card border-2 border-primary/30 shadow-neon flex items-center justify-center p-4 group-hover:glow-violet transition-all duration-300">
                    <item.icon className={`w-9 h-9 ${item.color}`} />
                  </div>
                  <span className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-gradient-to-br from-primary to-secondary text-primary-foreground text-sm font-bold flex items-center justify-center shadow-neon">
                    {item.step}
                  </span>
                </div>
                <h3 className="text-lg font-bold text-foreground mb-2">{item.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{item.desc}</p>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* ===== GALERIE D'IMAGES (humain & confiance) ===== */}
      <HomeImageGallery />

      {/* ===== OFFRE CLAIRE ===== */}
      <section className="py-16 lg:py-20 relative overflow-hidden bg-section-violet">
        <SectionFloatingIcons variant={3} />
        <div className="neon-orb neon-orb-blue w-[300px] h-[300px] top-0 right-0" />
        <div className="relative w-full px-4 md:px-6 lg:px-8">
          <AnimatedSection className="text-center mb-10">
            <h2 className="font-display text-3xl md:text-4xl font-extrabold text-foreground mb-3 tracking-tight">
              Tarifs <span className="text-gradient-neon">simples</span>
            </h2>
          </AnimatedSection>
          <div className="max-w-3xl mx-auto mb-6">
            <FreeModeBanner />
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {/* Parents - Free */}
            <AnimatedSection delay={0} animation="fade-up">
              <div className="card-glass-premium rounded-2xl p-8 text-center h-full relative group">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="bg-gradient-to-r from-accent to-accent/80 text-accent-foreground text-xs font-bold px-4 py-1.5 rounded-full shadow-neon glow-blue">
                    Gratuit
                  </span>
                </div>
                <div className="w-16 h-16 rounded-2xl bg-accent/25 border border-accent/40 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 glow-blue">
                  <Users className="w-8 h-8 text-accent" />
                </div>
                <h3 className="text-xl font-extrabold text-foreground mb-1">Parents</h3>
                <p className="text-3xl font-black text-gradient-neon mb-1">100% Gratuit</p>
                <p className="text-muted-foreground text-sm mb-5">Recherche, réservation, messagerie</p>
                <ul className="text-sm text-left space-y-2.5 mb-6">
                  {["Recherche illimitée", "Réservation directe", "Messagerie sécurisée", "Avis vérifiés"].map((t) => (
                    <li key={t} className="flex items-center gap-2.5">
                      <CheckCircle2 className="w-4 h-4 text-accent flex-shrink-0" />
                      <span className="text-foreground">{t}</span>
                    </li>
                  ))}
                </ul>
                <Link to={user ? "/search" : "/auth?tab=register&role=parent"}>
                  <Button className="w-full rounded-2xl h-11 font-bold btn-neon shadow-neon bg-gradient-to-r from-accent to-accent/80 text-accent-foreground hover:opacity-90">
                    Commencer gratuitement
                  </Button>
                </Link>
              </div>
            </AnimatedSection>

            {/* Pros */}
            <AnimatedSection delay={200} animation="fade-up">
              <div className="card-glass-premium rounded-2xl p-8 text-center h-full relative group border-primary/25">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="bg-gradient-to-r from-primary to-secondary text-primary-foreground text-xs font-bold px-4 py-1.5 rounded-full shadow-neon glow-violet">
                    Pro
                  </span>
                </div>
                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 glow-violet">
                  <Briefcase className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-extrabold text-foreground mb-1">Professionnels</h3>
                <p className="text-muted-foreground text-sm mb-4">Choisissez la formule qui vous convient</p>
                <div className="grid grid-cols-2 gap-2 mb-5">
                  <div className="rounded-xl border border-primary/20 bg-primary/5 p-3">
                    <p className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground mb-1">Mensuel</p>
                    <FreePriceTag hideBadge>
                      <span className="text-xl font-black text-foreground">{formatPrice(proMonthlyPrice)} €<span className="text-xs font-semibold text-muted-foreground">/mois</span></span>
                    </FreePriceTag>
                    <p className="text-[11px] text-muted-foreground mt-1">Sans engagement</p>
                  </div>
                  <div className="relative rounded-xl border-2 border-secondary/35 bg-secondary/10 p-3">
                    {proAnnualSavingsPercent > 0 && (
                      <span className="absolute -top-2 right-2 rounded-full bg-secondary px-2 py-0.5 text-[9px] font-black text-secondary-foreground">
                        -{proAnnualSavingsPercent}%
                      </span>
                    )}
                    <p className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground mb-1">Annuel</p>
                    <FreePriceTag hideBadge>
                      <span className="text-xl font-black text-gradient-neon">{formatPrice(proAnnualMonthlyPrice)} €<span className="text-xs font-semibold text-muted-foreground">/mois</span></span>
                    </FreePriceTag>
                    <p className="text-[11px] text-muted-foreground mt-1">{formatPrice(proAnnualTotal)} €/an</p>
                  </div>
                </div>
                <p className="text-muted-foreground text-sm mb-5">Visibilité, demandes, outils pro</p>
                <ul className="text-sm text-left space-y-2.5 mb-6">
                  {["Profil visible par tous", "Réception de demandes", "Messagerie directe", "Badge professionnel"].map((t) => (
                    <li key={t} className="flex items-center gap-2.5">
                      <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
                      <span className="text-foreground">{t}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/auth?tab=register&role=pro">
                  <Button className="w-full rounded-2xl h-11 font-bold btn-neon shadow-neon bg-gradient-to-r from-primary to-secondary hover:opacity-90">
                    Devenir prestataire
                  </Button>
                </Link>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* ===== CONFIANCE / TRUST ===== */}
      <section className="py-20 lg:py-28 relative overflow-hidden bg-section-pink">
        <SectionFloatingIcons variant={4} />
        <div className="neon-orb neon-orb-violet w-[500px] h-[500px] -top-[150px] -left-[100px]" />
        <div className="neon-orb neon-orb-pink w-[400px] h-[400px] -bottom-[100px] -right-[80px]" />

        <div className="relative w-full px-4 md:px-6 lg:px-8">
          <AnimatedSection className="text-center mb-14 max-w-2xl mx-auto">
            <div className="inline-flex items-center gap-2 card-glass-premium px-4 py-2 rounded-full text-xs font-semibold text-primary mb-5">
              <Shield className="w-3.5 h-3.5" />
              Confiance & Sécurité
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-extrabold text-foreground mb-4 tracking-tight">
              La <span className="text-gradient-neon">tranquillité d'esprit</span><br />à chaque étape
            </h2>
            <p className="text-muted-foreground text-lg leading-relaxed">
              Une plateforme pensée pour les familles exigeantes.
            </p>
          </AnimatedSection>

          <div className="max-w-6xl mx-auto grid lg:grid-cols-[1fr_auto_1fr] gap-10 lg:gap-8 items-center">
            {/* Left cards */}
            <div className="space-y-4">
              {[
                { icon: BadgeCheck, title: "Profils vérifiés manuellement", desc: "Identité contrôlée par notre équipe.", color: "text-primary", glow: "glow-violet" },
                { icon: MousePointerClick, title: "Réservation simple et rapide", desc: "Trouvez et réservez en quelques clics.", color: "text-secondary", glow: "glow-pink" },
              ].map((item, i) => (
                <AnimatedSection key={item.title} delay={i * 100} animation="fade-up">
                  <div className="card-glass-premium rounded-2xl p-5 flex items-start gap-4 group hover:border-primary/40 transition-all duration-300">
                    <div className={`flex-shrink-0 w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center ${item.glow} group-hover:scale-110 transition-transform`}>
                      <item.icon className={`w-6 h-6 ${item.color}`} />
                    </div>
                    <div className="text-left">
                      <h3 className="font-bold text-foreground mb-1">{item.title}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                </AnimatedSection>
              ))}
            </div>

            {/* Phone mockup center */}
            <AnimatedSection animation="scale" delay={200} className="flex justify-center">
              <div className="relative">
                {/* Glow halo */}
                <div className="absolute inset-0 bg-gradient-to-br from-primary/40 via-secondary/30 to-accent/40 blur-3xl scale-110 animate-pulse" />

                {/* Phone frame */}
                <div className="relative w-[260px] h-[520px] rounded-[3rem] bg-gradient-to-br from-foreground/90 to-foreground p-3 shadow-neon border border-primary/30">
                  {/* Notch */}
                  <div className="absolute top-3 left-1/2 -translate-x-1/2 w-24 h-6 bg-background rounded-b-2xl z-20" />

                  {/* Screen */}
                  <div className="w-full h-full rounded-[2.3rem] bg-gradient-to-br from-background via-background to-primary/10 overflow-hidden relative flex flex-col items-center justify-center p-6">
                    <div className="absolute top-12 left-0 right-0 flex justify-center">
                      <div className="card-glass-premium px-3 py-1.5 rounded-full text-[10px] font-bold text-primary flex items-center gap-1">
                        <Sparkles className="w-2.5 h-2.5" />
                        Click&Gard
                      </div>
                    </div>

                    <div className="relative flex flex-col items-center gap-4">
                      {/* Pulsing rings */}
                      <div className="relative w-32 h-32 flex items-center justify-center">
                        <div className="absolute inset-0 rounded-full border-2 border-primary/40 animate-ping" />
                        <div className="absolute inset-2 rounded-full border-2 border-secondary/40 animate-ping" style={{ animationDelay: "0.5s" }} />
                        <div className="absolute inset-4 rounded-full border-2 border-accent/40 animate-ping" style={{ animationDelay: "1s" }} />

                        <div className="relative w-20 h-20 rounded-full bg-gradient-to-br from-primary via-secondary to-primary flex items-center justify-center shadow-neon glow-violet animate-pulse">
                          <MousePointerClick className="w-9 h-9 text-primary-foreground" />
                        </div>
                      </div>

                      <div className="text-center">
                        <p className="font-display font-extrabold text-foreground text-base">Garde en 1 click</p>
                        <p className="text-[10px] text-muted-foreground mt-0.5">Touchez pour réserver</p>
                      </div>

                      <div className="card-glass-premium rounded-xl p-3 w-full mt-2 flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-secondary flex-shrink-0" />
                        <div className="flex-1 text-left">
                          <div className="h-1.5 w-16 bg-foreground/20 rounded mb-1" />
                          <div className="h-1 w-12 bg-foreground/10 rounded" />
                        </div>
                        <CheckCircle2 className="w-4 h-4 text-accent flex-shrink-0" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Floating badges */}
                <div className="absolute -left-6 top-20 card-glass-premium rounded-xl px-3 py-2 items-center gap-2 glow-violet animate-float hidden md:flex">
                  <BadgeCheck className="w-4 h-4 text-primary" />
                  <span className="text-xs font-bold text-foreground">Vérifié</span>
                </div>
                <div className="absolute -right-6 bottom-32 card-glass-premium rounded-xl px-3 py-2 items-center gap-2 glow-pink animate-float hidden md:flex" style={{ animationDelay: "1.5s" }}>
                  <Lock className="w-4 h-4 text-secondary" />
                  <span className="text-xs font-bold text-foreground">Sécurisé</span>
                </div>
              </div>
            </AnimatedSection>

            {/* Right cards */}
            <div className="space-y-4">
              {[
                { icon: MapPin, title: "Garde d'enfants près de chez vous", desc: "Des pros vérifiés dans votre quartier.", color: "text-accent", glow: "glow-blue" },
                { icon: Lock, title: "Mise en relation directe", desc: "0% commission : vous réglez la nounou directement.", color: "text-primary", glow: "glow-violet" },
              ].map((item, i) => (
                <AnimatedSection key={item.title} delay={i * 100} animation="fade-up">
                  <div className="card-glass-premium rounded-2xl p-5 flex items-start gap-4 group hover:border-primary/40 transition-all duration-300">
                    <div className={`flex-shrink-0 w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center ${item.glow} group-hover:scale-110 transition-transform`}>
                      <item.icon className={`w-6 h-6 ${item.color}`} />
                    </div>
                    <div className="text-left">
                      <h3 className="font-bold text-foreground mb-1">{item.title}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== CTA FINAL ===== */}
      <section className="py-20 lg:py-24 relative overflow-hidden">
        <SectionFloatingIcons variant={5} />
        <div className="neon-orb neon-orb-violet w-[500px] h-[500px] -top-[100px] left-1/4" />
        <div className="neon-orb neon-orb-pink w-[400px] h-[400px] -bottom-[100px] right-1/4" />
        <AnimatedSection className="relative w-full px-4 md:px-6 lg:px-8 text-center max-w-2xl mx-auto" animation="scale">
          <h2 className="font-display text-3xl md:text-4xl font-extrabold text-foreground mb-4 tracking-tight">
            Prêt à <span className="text-gradient-neon">commencer</span> ?
          </h2>
          <p className="text-muted-foreground text-lg mb-8 leading-relaxed">
            Rejoignez Click&Gard et trouvez la garde idéale pour vos enfants.
          </p>
          <Link to={user ? "/search" : "/auth?tab=register&role=parent"}>
            <Button size="lg" className="rounded-2xl px-12 font-bold shadow-neon h-14 text-base bg-gradient-to-r from-primary via-secondary to-primary hover:opacity-90 transition-all duration-300 btn-neon btn-neon-pulse">
              <Search className="w-5 h-5 mr-2" /> Trouver une garde maintenant
            </Button>
          </Link>
        </AnimatedSection>
      </section>

      {/* Footer */}
      <footer className="border-t border-primary/10 py-10 bg-gradient-to-b from-transparent to-muted/20">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2.5">
              <img alt="Click&Gard" className="w-10 h-10 rounded-xl object-cover shadow-neon" src="/lovable-uploads/2236e7c0-4083-436f-a95c-06d4486b46d6.png" />
              <span className="text-sm font-bold text-foreground">
                Click<span className="text-gradient-static">&</span>Gard
              </span>
              <span className="text-xs text-muted-foreground">© 2026</span>
            </div>
            <div className="flex gap-6 text-sm text-muted-foreground">
              <Link to="/mentions-legales" className="link-hover transition-colors font-medium">Mentions légales</Link>
              <Link to="/confidentialite" className="link-hover transition-colors font-medium">Confidentialité</Link>
              <Link to="/cgu" className="link-hover transition-colors font-medium">CGU</Link>
              <Link to="/contact" className="link-hover transition-colors font-medium">Contact</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;
